import { useEffect, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Combobox, SwitchTrack } from "../../../../../../components/ui";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import type { ThemeClasses, PointCoordinate } from "../types";
import AddressAutocomplete from "../../../../../../components/base/AddressAutocomplete";
import { CircularCoordinatesForm, CircularRadiusForm } from "./forms/CircularCoordinatesForm";
import { LinearCoordinatesForm } from "./forms/LinearCoordinatesForm";
import { PolygonalCoordinatesForm } from "./forms/PolygonalCoordinatesForm";
import { getGeofenceTypeOptions } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { TIMEZONE_OPTIONS } from "../../trips/types";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";

interface GeofenceFormProps {
  isFromTracking?: boolean;
  isLinearOnly?: boolean;
  isGeofencesOnly?: boolean;
  hideNotificationFields?: boolean;
  form: UseFormReturn<Geofence>;
  shape: string;
  isEdit: boolean;
  loadingSubmit: boolean;
  hasRealChanges: () => boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  editGeofence: Geofence | null;
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  linearPoints: PointCoordinate[];
  setLinearPoints: (points: PointCoordinate[]) => void;
  defaultCircularCoordinates: {
    latitude: number;
    longitude: number;
    radius: number;
  };
  onBufferChange: (value: number) => void;
  linearAddresses: { address: string | null; geocoding: boolean }[];
  onLinearAddressEdit: (index: number, address: string) => void;
  onAddressSelect: (address: string, lat: number, lng: number) => void;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  // Mapa renderizado por el modal; se coloca en la columna derecha del form.
  mapSlot?: ReactNode;
}

export function GeofenceForm({
  isFromTracking,
  isLinearOnly = false,
  isGeofencesOnly = false,
  hideNotificationFields = false,
  form,
  shape,
  isEdit,
  loadingSubmit,
  hasRealChanges,
  themeClasses,
  isDarkMode,
  editGeofence,
  polygonPoints,
  setPolygonPoints,
  linearPoints,
  setLinearPoints,
  defaultCircularCoordinates,
  onBufferChange,
  linearAddresses,
  onLinearAddressEdit,
  onAddressSelect,
  onSubmit,
  onCancel,
  mapSlot,
}: GeofenceFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
  } = form;

  // Opciones para selects
  const typeOptions = getGeofenceTypeOptions(t);
  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptions = getStatusOptionsForEntity(statuses, "geofences", t);

  // Vehicles (units), trailers and users data
  const { data: devices = [] } = useAllDevices();
  // Lightweight users (id + name + email only) for the notify select.
  const { usersLight, fetchUsersLight } = useUsersStore();
  const { isReseller } = useIsReseller();

  useEffect(() => {
    // Resellers can notify users from any tenant; bypass the company filter so
    // the dropdown lists every user instead of just the active company.
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchUsersLight, isReseller]);

  const deviceOptions = devices.map((d) => {
    const vehicleName = d.vehicle ? d.vehicle?.name : "";
    const trailerName = d.remolque ? d.remolque?.name : "";
    const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
    return {
      value: d.id,
      label: `${nameDevice}${vehicleName ? ` (Vehículo: ${vehicleName})` : ""}${trailerName ? ` (Remolque: ${trailerName})` : ""}`,
    };
  });

  const userOptions = usersLight.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const timezoneOptions = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
    value,
    label,
  }));

  const inputClassName = `w-full min-h-10 px-3 py-2 text-xs bg-app border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  const stackGap = "gap-2.5";
  const fieldGap = "gap-1.5";
  const labelClassName = `block text-sm font-medium ${themeClasses.text}`;

  const originalShape = editGeofence?.shape;
  // Al editar, la forma queda fija: cada una guarda las coordenadas con una
  // estructura distinta (circular es un punto + radio, lineal son 2 puntos con
  // eje denso y buffer), asi que cambiarla a media edicion enviaria basura.
  const isShapeLocked = (target: string) =>
    isEdit && !!originalShape && originalShape !== target;
  const circularLocked = isShapeLocked("circular");
  const polygonalLocked = isShapeLocked("polygonal");
  const linearLocked = isShapeLocked("linear");
  const lockedShapeTitle = t(
    "geofences.form.shapeLockedOnEdit",
    "No se puede cambiar entre circular y poligonal al editar"
  );

  const timezoneField = (
    <div className={`flex flex-col ${fieldGap}`}>
      <label className={labelClassName}>
        {t("geofences.fields.timezone", "Zona horaria")}
      </label>
      <RHFController
        name="timezone"
        control={control}
        render={({ field }) => (
          <Combobox
            options={timezoneOptions}
            placeholder={t("geofences.form.selectTimezone", "Seleccionar zona horaria")}
            value={
              timezoneOptions.find((opt) => opt.value === field.value) ||
              timezoneOptions.find((opt) => opt.value === "America/Mexico_City")
            }
            onChange={(option: any) =>
              field.onChange(option?.value || "America/Mexico_City")
            }
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        )}
      />
    </div>
  );

  const addressField = (
    <div className={`flex flex-col ${fieldGap}`}>
      <label className={labelClassName}>
        {t("geofences.fields.address", "Dirección")} <span className="text-error">*</span>
      </label>
      <AddressAutocomplete
        key={`address-${shape}`}
        onAddressSelect={onAddressSelect}
        onAddressTextChange={(text) => {
          setValue("address", text, { shouldValidate: true, shouldDirty: true });
        }}
        placeholder={t("geofences.form.addressPlaceholder", "Ej: Calle Principal #123, Ciudad, País")}
        defaultValue={editGeofence?.address || ""}
        value={watch("address") ?? ""}
        className={`${inputClassName} focus:outline-none focus:ring-2 focus:ring-primary-500`}
      />
      <input {...register("address", { required: true })} type="hidden" />
      {errors.address && (
        <span className="text-error text-xs ml-3">
          {t("geofences.form.requiredField", "Este campo es obligatorio")}
        </span>
      )}
    </div>
  );

  const mapBlock = mapSlot ? (
    <div className="flex flex-col gap-2">
      {/* En lineal el buscador general no aporta: la direccion que importa es
          la del punto de inicio, que ya tiene su propio input con autocompletado
          en LinearCoordinatesForm y alimenta el campo `address` del formulario. */}
      {shape !== "linear" && addressField}
      <div className="h-[240px] sm:h-[340px] w-full overflow-hidden rounded-xl border border-line">
        {mapSlot}
      </div>
      {shape === "linear" && (
        <LinearCoordinatesForm
          linearPoints={linearPoints}
          setLinearPoints={setLinearPoints}
          setValue={setValue}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          buffer={Number(watch("buffer")) || LINEAR_BUFFER_DEFAULT}
          onBufferChange={onBufferChange}
          linearAddresses={linearAddresses}
          onAddressEdit={onLinearAddressEdit}
        />
      )}
      {shape === "circular" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CircularCoordinatesForm
              compact
              register={register}
              watch={watch}
              trigger={trigger}
              errors={errors}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              defaultLatitude={defaultCircularCoordinates.latitude}
              defaultLongitude={defaultCircularCoordinates.longitude}
            />
          </div>
          <CircularRadiusForm
            register={register}
            watch={watch}
            trigger={trigger}
            errors={errors}
            themeClasses={themeClasses}
            compact
          />
        </>
      )}
    </div>
  ) : null;

  return (
    <form
      className="flex flex-col w-full gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={`flex flex-col ${stackGap}`}>
        {mapBlock}

        {/* ═══════ Columna izquierda: datos de la geocerca ═══════ */}
        <div className="contents">
      {/* Botones de selección de forma geométrica */}
      <div className={`w-full flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>
          {t("geofences.fields.shape", "Forma geométrica")}
        </label>
        <div className="flex items-center w-full gap-2">
          {!isFromTracking && !isLinearOnly && (
            <button
              type="button"
              onClick={() => { setValue("shape", "circular"); trigger("shape"); }}
              disabled={circularLocked}
              title={circularLocked ? lockedShapeTitle : undefined}
              className={`flex-1 h-8 text-xs flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${
                shape === "circular"
                  ? "bg-primary-500 text-on-primary"
                  : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
              } ${circularLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <i className="ri-circle-line 2xl:text-lg"></i>
              <span>{t("geofences.shapes.circular", "Circular")}</span>
            </button>
          )}
          {!isFromTracking && !isLinearOnly && (
            <button
              type="button"
              onClick={() => { setValue("shape", "polygonal"); trigger("shape"); }}
              disabled={polygonalLocked}
              title={polygonalLocked ? lockedShapeTitle : undefined}
              className={`flex-1 h-8 text-xs flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${
                shape === "polygonal"
                  ? "bg-primary-500 text-on-primary"
                  : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
              } ${polygonalLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <i className="ri-pentagon-line 2xl:text-lg"></i>
              <span>{t("geofences.shapes.polygonal", "Poligonal")}</span>
            </button>
          )}
          {!isGeofencesOnly && (
          <button
            type="button"
            onClick={() => { setValue("shape", "linear"); trigger("shape"); }}
            disabled={isLinearOnly || linearLocked}
            title={linearLocked ? lockedShapeTitle : undefined}
            className={`flex-1 h-8 text-xs flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${
              shape === "linear"
                ? "bg-primary-500 text-on-primary"
                : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
            } ${isLinearOnly ? "cursor-default" : ""} ${
              linearLocked ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <i className="ri-route-line 2xl:text-lg"></i>
            <span>{t("geofences.shapes.linear", "Lineal")}</span>
          </button>
          )}
        </div>
        <input type="hidden" {...register("shape", { required: true })} />
        {errors.shape && (
          <span className="text-error text-xs">
            {t("geofences.form.selectShapeRequired", "Debe seleccionar una forma geométrica")}
          </span>
        )}
      </div>

      {/* Línea divisoria */}
      {/* Nombre */}
      <div className={`flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>
          {t("geofences.fields.name", "Nombre de la geocerca")} <span className="text-error">*</span>
        </label>
        <input
          {...register("name", { required: true })}
          name="name"
          type="text"
          className={`${inputClassName} ${
            errors.name
              ? "focus:outline-none ring-2 ring-error"
              : "focus:outline-none focus:ring-2 focus:ring-primary-500"
          }`}
          placeholder={t("geofences.form.namePlaceholder", "Ej: Centro de Distribución Norte")}
        />
        {errors.name && (
          <span className="text-error text-xs ml-3">
            {t("geofences.form.requiredField", "Este campo es obligatorio")}
          </span>
        )}
      </div>

      {/* Dirección */}
      {!mapSlot && shape !== "linear" && addressField}

      {/* Tipo y Estado */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={`flex flex-col ${fieldGap}`}>
          <label className={labelClassName}>
            {t("geofences.fields.type", "Tipo")} <span className="text-error">*</span>
          </label>
          <RHFController
            name="type"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Combobox
                {...field}
                options={typeOptions}
                placeholder={t("geofences.form.selectType", "Seleccionar tipo")}
                value={typeOptions.find((opt) => opt.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value)}
                isSearchable
                noOptionsMessage={() => t("geofences.form.noTypesAvailable", "Sin tipos disponibles")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.type && (
            <span className="text-error text-xs">
              {t("geofences.form.requiredField", "Campo obligatorio")}
            </span>
          )}
        </div>
        <div className={`flex flex-col ${fieldGap}`}>
          <label className={labelClassName}>
            {t("geofences.fields.status", "Estado")} <span className="text-error">*</span>
          </label>
          <RHFController
            name="status_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Combobox
                {...field}
                options={statusOptions}
                placeholder={t("geofences.form.selectStatus", "Seleccionar estado")}
                value={statusOptions.find((opt: any) => opt.value === field.value)}
                onChange={(option: any) => field.onChange(option?.value)}
                isSearchable
                noOptionsMessage={() => t("geofences.form.noStatusAvailable", "Sin estados disponibles")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.status_id && (
            <span className="text-error text-xs">
              {t("geofences.form.requiredField", "Campo obligatorio")}
            </span>
          )}
        </div>
      </div>

      {/* Descripción */}
      <div className={`flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>
          {t("geofences.fields.description", "Descripción")} <span className="text-error">*</span>
        </label>
        <textarea
          {...register("description", { required: true })}
          className={`${inputClassName} ${
            errors.description
              ? "focus:outline-none ring-2 ring-error"
              : "focus:outline-none focus:ring-2 focus:ring-primary-500"
          } resize-none`}
          rows={2}
          placeholder={t("geofences.form.descriptionPlaceholder", "Descripción de la geocerca y su propósito...")}
        />
        {errors.description && (
          <span className="text-error text-xs ml-3">
            {t("geofences.form.requiredField", "Campo obligatorio")}
          </span>
        )}
      </div>

      {/* Disparadores: entrada / salida (geofenceActions) */}
      <div className={`flex flex-col ${fieldGap}`}>
        <label className={labelClassName}>
          {t("geofences.fields.crossingTriggers", "Eventos al cruzar")}
        </label>
        <RHFController
          name="geofenceActions"
          control={control}
          render={({ field }) => {
            const value = (field.value as ("entry" | "exit")[] | undefined) ?? [];
            const toggle = (key: "entry" | "exit") =>
              field.onChange(
                value.includes(key) ? value.filter((a) => a !== key) : [...value, key]
              );
            const CROSSING_ACTIONS = [
              {
                key: "entry",
                icon: "ri-login-circle-line",
                label:
                  shape === "linear"
                    ? t("geofences.fields.entryCorridor", "Entrada al corredor")
                    : t("geofences.fields.entry", "Entrada"),
              },
              {
                key: "exit",
                icon: "ri-logout-circle-line",
                label:
                  shape === "linear"
                    ? t("geofences.fields.exitCorridor", "Salida del corredor")
                    : t("geofences.fields.exit", "Salida"),
              },
            ] as const;
            return (
              <div className="flex flex-wrap w-full gap-2">
                {CROSSING_ACTIONS.map(({ key, icon, label }) => {
                  const active = value.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggle(key)}
                      className={`flex flex-1 items-center justify-center gap-2 border font-medium transition-all h-8 min-w-[7rem] rounded-lg text-xs ${
                        active
                          ? "bg-primary-500 border-primary-400 text-on-primary"
                          : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                      }`}
                    >
                      <i className={icon} aria-hidden />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            );
          }}
        />
      </div>

      {/* Dispositivos asignados */}
      <div className={`flex-col ${fieldGap} ${hideNotificationFields ? "hidden" : "flex"}`}>
        <label className={labelClassName}>
          {t("geofences.fields.devices", "Dispositivos asignados")}
        </label>
        <p className={`text-xs text-content-muted -mt-1 mb-1`}>
          {t("geofences.form.devicesHint", "Deja vacío para aplicar a todos los dispositivos de la empresa")}
        </p>
        <RHFController
          name="devices"
          control={control}
          render={({ field }) => (
            <Combobox
              isMulti
              options={deviceOptions}
              placeholder={t("geofences.form.selectDevices", "Seleccionar dispositivos...")}
              value={deviceOptions.filter((opt) =>
                (field.value as string[] | undefined)?.includes(opt.value)
              )}
              onChange={(selected) =>
                field.onChange(
                  selected
                    ? selected.map((s) => s.value)
                    : []
                )
              }
              isClearable
              isSearchable
              noOptionsMessage={() =>
                t("geofences.form.noDevicesAvailable", "Sin dispositivos disponibles")
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
      </div>

      {/* Usuarios notificados */}
      <div className={`flex-col gap-2 ${hideNotificationFields ? "hidden" : "flex"}`}>
        <label
          className={`block text-sm font-medium ${themeClasses.text} mb-1`}
        >
          {t("geofences.fields.notifiedUsers", "Usuarios a notificar")}
        </label>
        <p className={`text-xs text-content-muted -mt-1 mb-1`}>
          {t("geofences.form.notifiedUsersHint", "Selecciona usuarios existentes")}
        </p>
        <RHFController
          name="notifiedUsers"
          control={control}
          render={({ field }) => {
            const currentValues = (field.value as string[] | undefined) || [];
            const selectedOptions = currentValues.map((val) => {
              const existing = userOptions.find((o) => o.value === val);
              return existing || { value: val, label: val };
            });
            return (
              <Combobox
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={userOptions}
                placeholder={t(
                  "geofences.form.selectUsers",
                  "Seleccionar usuarios..."
                )}
                value={selectedOptions}
                onChange={(selected) =>
                  field.onChange(
                    selected
                      ? selected.map((s) => s.value)
                      : []
                  )
                }
                isClearable
                isSearchable
                noOptionsMessage={() =>
                  t(
                    "geofences.form.noUsersAvailable",
                    "Sin usuarios disponibles"
                  )
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            );
          }}
        />
      </div>

      {/* Envío de correos además del push */}
      <div className={`flex-col gap-2 ${hideNotificationFields ? "hidden" : "flex"}`}>
        <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
          {t("geofences.fields.sendEmails")}
        </label>
        <p className={`text-xs text-content-muted -mt-1 mb-1`}>
          {t("geofences.form.sendEmailsHint")}
        </p>
        <RHFController
          name="sendEmails"
          control={control}
          render={({ field }) => {
            const active = field.value === true;
            return (
              <button
                type="button"
                role="switch"
                aria-checked={active}
                onClick={() => field.onChange(!active)}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  active
                    ? "bg-primary-500/10 border-primary-400 text-primary-600"
                    : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                }`}
              >
                <span className="flex items-center gap-2">
                  <i className="ri-mail-send-line 2xl:text-lg" aria-hidden />
                  {active
                    ? t("geofences.form.sendEmailsOn")
                    : t("geofences.form.sendEmailsOff")}
                </span>
                <SwitchTrack checked={active} size="sm" />
              </button>
            );
          }}
        />
      </div>
        </div>

        {/* ═══════ Columna derecha: mapa + zona horaria/coordenadas ═══════ */}
        <div className="contents">
      {timezoneField}

      {shape === "polygonal" && (
        <PolygonalCoordinatesForm
          polygonPoints={polygonPoints}
          setPolygonPoints={setPolygonPoints}
          setValue={setValue}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
        />
      )}

        </div>
      </div>

      <div className="flex gap-3 border-t border-line pt-4 mt-2">
        <Button
          type="button"
          variant="outline"
          shape="pill"
          className="flex-1"
          onClick={onCancel}
        >
          {t("geofences.actions.cancel", "Cancelar")}
        </Button>
        <Button
          type="submit"
          shape="pill"
          className="flex-1"
          loading={loadingSubmit}
          disabled={!isValid || (isEdit && !hasRealChanges())}
        >
          {loadingSubmit
            ? isEdit
              ? t("geofences.form.submitting", "Guardando...")
              : t("geofences.form.creating", "Creando...")
            : isEdit
            ? t("geofences.actions.saveChanges", "Guardar Cambios")
            : t("geofences.actions.create", "Crear Geocerca")}
        </Button>
      </div>
    </form>
  );
}
